import { Component, type ErrorInfo, type ReactNode } from 'react'
import { i18n } from '@tarifdata/i18n'

interface AppErrorBoundaryProps {
  children: ReactNode
}

interface AppErrorBoundaryState {
  error: Error | null
}

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled TMA application error', error, info.componentStack)
  }

  private reset = () => {
    this.setState({ error: null })
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <main className="flex min-h-screen items-center bg-tg-bg p-4 text-tg-text" role="alert">
        <div className="mx-auto w-full max-w-md rounded-2xl bg-tg-secondaryBg p-6 text-center shadow-sm">
          <div className="mb-3 text-5xl" aria-hidden="true">⚠️</div>
          <h1 className="text-xl font-bold">{i18n.t('errors.appError')}</h1>
          <p className="mt-2 text-sm text-tg-hint">{i18n.t('errors.unexpected')}</p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-xl bg-tg-button px-4 py-2 font-semibold text-tg-buttonText"
            >
              {i18n.t('errors.tryAgain')}
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-xl border border-tg-hint/30 px-4 py-2 font-semibold"
            >
              {i18n.t('errors.reload')}
            </button>
          </div>
        </div>
      </main>
    )
  }
}
